import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'src/easyclaim.app.libs/queryKeys';
import { isAbortError, reportError } from 'src/easyclaim.app.libs/reportError';
import { uploadDocument } from 'src/easyclaim.app.services/DocumentService';
import { usePhotoThumbnailStore } from 'src/easyclaim.app.stores/usePhotoThumbnailStore';

import { useIsPerformanceEnhancerEnabled } from './useIsPerformanceEnhancerEnabled';

/**
 * Uploads a photo/document for a case.
 * On success, the response's `documentId` should be used to enable {@link useThumbnail}.
 *
 * Gated by the `canUseExperimentalPerformanceEnhancer` customer flag: when disabled,
 * `mutate`/`mutateAsync` reject immediately instead of calling the CIAPI backend.
 * Check `isEnabled` before invoking the mutation to avoid triggering it needlessly.
 */
export function useUploadDocument() {
	const isEnabled = useIsPerformanceEnhancerEnabled();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		networkMode: 'always',
		mutationFn: (params: Parameters<typeof uploadDocument>[0]) => {
			if (!isEnabled) {
				return Promise.reject(
					new Error(
						'useUploadDocument is disabled: canUseExperimentalPerformanceEnhancer is off for this customer.'
					)
				);
			}

			return uploadDocument(params);
		},
		onSuccess: (response, params) => {
			const documentId = response.documentId ?? params.documentId;

			if (!documentId) {
				return;
			}

			usePhotoThumbnailStore.getState().clearThumbnail(documentId);
			queryClient.removeQueries({
				queryKey: queryKeys.thumbnails.byImage(params.caseId, documentId),
			});
		},
		onError: (error, params) => {
			if (!isEnabled || isAbortError(error)) {
				return;
			}

			reportError(error, {
				context: 'useUploadDocument',
				caseId: params.caseId,
				documentId: params.documentId,
			});
		},
	});

	return { ...mutation, isEnabled };
}
